import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { resolveStoryBucketBaseUrl } from './resolve-story-bucket-base-url';

export interface PublicStoryBucketEnv {
  publicStoryBucketEndpoint: string;
  publicStoryBucketName: string;
}

/** YYYY/MM/DD segment derived from the canonical UTC date encoded in the story timestamp. */
export function storyDatePathFromIso(createdAtIso: string): string | null {
  const isoDateMatch = createdAtIso.match(
    /^(\d{4})-(\d{2})-(\d{2})T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/
  );
  if (isoDateMatch) {
    const [, year, month, day] = isoDateMatch;
    return `${year}/${month}/${day}`;
  }

  const d = new Date(createdAtIso);
  if (Number.isNaN(d.getTime())) {
    return null;
  }
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
}

export function getPublicStoryBucketBaseUrl(env: PublicStoryBucketEnv): string | null {
  return resolveStoryBucketBaseUrl(
    env.publicStoryBucketEndpoint,
    env.publicStoryBucketName
  );
}

export interface PublicStoryAssetUrls {
  baseUrl: string;
  audioUrl: string;
  textUrl: string;
  vttUrl: string;
  imageUrl: string;
  imageThumbUrl: string;
}

function assetUrlsFromBase(baseUrl: string, storyId: string): PublicStoryAssetUrls {
  const root = baseUrl.replace(/\/+$/, '');
  return {
    baseUrl: root,
    audioUrl: `${root}/${storyId}.mp3`,
    textUrl: `${root}/${storyId}.txt`,
    vttUrl: `${root}/${storyId}.vtt`,
    imageUrl: `${root}/${storyId}.jpg`,
    imageThumbUrl: `${root}/${storyId}_thumb.jpg`,
  };
}

/** Virtual-hosted DO Spaces URL (often more reliable than path-style on native iOS). */
function buildVhostBucketBaseUrl(
  env: PublicStoryBucketEnv,
  datePath: string
): string | null {
  const bucket = env.publicStoryBucketName?.trim();
  const endpoint = env.publicStoryBucketEndpoint?.trim();
  if (!bucket || !endpoint) {
    return null;
  }
  try {
    const host = new URL(endpoint).hostname;
    const region = host.split('.')[0];
    if (!region) {
      return null;
    }
    return `https://${bucket}.${region}.digitaloceanspaces.com/${datePath}`;
  } catch {
    return null;
  }
}

/**
 * Candidate UTC date folders for a share link timestamp. Release iOS builds can
 * deliver slightly different query strings than local Xcode / dev-server runs.
 */
export function shareDatePathCandidates(createdAtIso: string): string[] {
  const out: string[] = [];
  const add = (path: string | null) => {
    if (path && !out.includes(path)) {
      out.push(path);
    }
  };

  const trimmed = createdAtIso.trim();
  let decoded = trimmed;
  try {
    decoded = decodeURIComponent(trimmed);
  } catch {
    decoded = trimmed;
  }

  const variants = [
    trimmed,
    decoded,
    repairIsoTimestampPlusAsSpace(trimmed),
    repairIsoTimestampPlusAsSpace(decoded),
    normalizeShareCreatedAtParam(trimmed),
  ];

  for (const v of variants) {
    add(storyDatePathFromIso(v));
    const d = new Date(v);
    if (!Number.isNaN(d.getTime())) {
      add(storyDatePathFromIso(d.toISOString()));
    }
  }

  return out;
}

export function buildPublicStoryAssetUrls(
  env: PublicStoryBucketEnv,
  storyId: string,
  createdAtIso: string
): PublicStoryAssetUrls | null {
  const bucketBase = getPublicStoryBucketBaseUrl(env);
  const datePaths = shareDatePathCandidates(createdAtIso);
  if (!bucketBase || datePaths.length === 0) {
    return null;
  }
  return assetUrlsFromBase(
    `${bucketBase.replace(/\/+$/, '')}/${datePaths[0]}`,
    storyId
  );
}

/**
 * URLSearchParams / Universal Links treat `+` as space. Repair timestamps like
 * `2026-04-08T03:10:52.824 00:00` back to `...+00:00` before path parsing.
 */
export function repairIsoTimestampPlusAsSpace(value: string): string {
  return value.replace(
    /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?)\s+(\d{2}:\d{2})$/,
    '$1+$2'
  );
}

/**
 * Normalizes `createdAt` from share / deep-link query strings. Universal Links and
 * marketing redirects sometimes URL-encode or truncate ISO timestamps so strict
 * parsing fails and public S3 paths are built with the wrong date folder.
 */
export function normalizeShareCreatedAtParam(raw: string): string {
  const trimmed = raw.trim();
  let decoded = trimmed;
  try {
    decoded = decodeURIComponent(trimmed);
  } catch {
    decoded = trimmed;
  }
  decoded = repairIsoTimestampPlusAsSpace(decoded);
  if (storyDatePathFromIso(decoded)) {
    return decoded;
  }
  const parsed = new Date(decoded);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString();
  }
  return decoded;
}

function tryDecodeBase64Utf8(value: string): string | null {
  const compact = value.replace(/\s/g, '');
  if (compact.length < 8 || compact.length % 4 !== 0) {
    return null;
  }
  if (!/^[A-Za-z0-9+/=]+$/.test(compact)) {
    return null;
  }
  try {
    const decoded = atob(compact);
    if (!decoded.trim()) {
      return null;
    }
    return decoded;
  } catch {
    return null;
  }
}

/** Coerce CapacitorHttp / fetch bodies into a plain text string. */
export function coerceHttpResponseText(data: unknown): string {
  if (typeof data === 'string') {
    const asText = data.trim();
    if (!asText) {
      return '';
    }
    const fromB64 = tryDecodeBase64Utf8(asText);
    if (fromB64?.trim()) {
      return fromB64;
    }
    return data;
  }
  if (data == null) {
    return '';
  }
  if (data instanceof ArrayBuffer) {
    return new TextDecoder('utf-8').decode(new Uint8Array(data));
  }
  if (ArrayBuffer.isView(data)) {
    return new TextDecoder('utf-8').decode(
      new Uint8Array(data.buffer, data.byteOffset, data.byteLength)
    );
  }
  if (typeof data === 'object') {
    const maybe = data as {
      text?: string;
      body?: string;
      data?: unknown;
      value?: string;
    };
    if (typeof maybe.text === 'string') {
      return coerceHttpResponseText(maybe.text);
    }
    if (typeof maybe.body === 'string') {
      return coerceHttpResponseText(maybe.body);
    }
    if (typeof maybe.value === 'string') {
      return coerceHttpResponseText(maybe.value);
    }
    if (maybe.data != null) {
      return coerceHttpResponseText(maybe.data);
    }
  }
  return String(data);
}

const PUBLIC_STORY_HTTP_HEADERS = {
  Accept: 'text/plain, */*',
  'User-Agent': 'Storees/1.0 (Mobile)',
};

const PUBLIC_STORY_AUDIO_PROBE_HEADERS = {
  Accept: 'audio/mpeg, */*',
  'User-Agent': 'Storees/1.0 (Mobile)',
};

const PUBLIC_STORY_AUDIO_READY_TIMEOUT_MS = 2000;
const audioReadyCache = new Set<string>();

async function fetchTextViaCapacitorHttp(url: string): Promise<string> {
  const httpRes = await CapacitorHttp.request({
    url,
    method: 'GET',
    headers: PUBLIC_STORY_HTTP_HEADERS,
    responseType: 'text',
  });
  if (httpRes.status !== 200) {
    throw new Error(`Text HTTP ${httpRes.status}`);
  }
  const text = coerceHttpResponseText(httpRes.data);
  if (!text.trim()) {
    throw new Error('Empty text response body');
  }
  return text;
}

async function fetchTextViaWebFetch(url: string): Promise<string> {
  const res = await fetch(url, {
    headers: PUBLIC_STORY_HTTP_HEADERS,
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  const text = await res.text();
  if (!text.trim()) {
    throw new Error('Empty text response body');
  }
  return text;
}

/**
 * Fetch a public story text asset (.txt / .vtt).
 * On native release builds Capacitor patches `fetch`; CapacitorHttp is more reliable.
 */
export async function fetchPublicStoryAssetText(url: string): Promise<string> {
  const errors: unknown[] = [];

  if (Capacitor.isNativePlatform()) {
    try {
      return await fetchTextViaCapacitorHttp(url);
    } catch (nativeErr) {
      errors.push(nativeErr);
    }
  }

  try {
    return await fetchTextViaWebFetch(url);
  } catch (fetchErr) {
    errors.push(fetchErr);
  }

  if (errors.length > 0) {
    throw errors[0];
  }
  throw new Error('Unable to fetch story text');
}

function textUrlsForDatePath(
  env: PublicStoryBucketEnv,
  storyId: string,
  datePath: string
): string[] {
  const bucketBase = getPublicStoryBucketBaseUrl(env);
  if (!bucketBase) {
    return [];
  }
  const pathStyle = `${bucketBase.replace(/\/+$/, '')}/${datePath}/${storyId}.txt`;
  const out = [pathStyle];
  const vhostBase = buildVhostBucketBaseUrl(env, datePath);
  if (vhostBase) {
    const vhost = `${vhostBase}/${storyId}.txt`;
    if (!out.includes(vhost)) {
      out.push(vhost);
    }
  }
  return out;
}

/** Ordered text URLs (path-style then virtual-hosted) for share deep links. */
export function buildPublicStoryTextUrlCandidates(
  env: PublicStoryBucketEnv,
  storyId: string,
  createdAtIso: string
): string[] {
  const out: string[] = [];
  for (const datePath of shareDatePathCandidates(createdAtIso)) {
    for (const url of textUrlsForDatePath(env, storyId, datePath)) {
      if (!out.includes(url)) {
        out.push(url);
      }
    }
  }
  return out;
}

export interface PublicStoryUrlAttemptFailure {
  url: string;
  detail: string;
}

export class PublicStoryTextFetchError extends Error {
  readonly urlAttempts: PublicStoryUrlAttemptFailure[];

  constructor(
    message: string,
    readonly attemptedUrls: string[],
    readonly causes: unknown[],
    urlAttempts?: PublicStoryUrlAttemptFailure[]
  ) {
    super(message);
    this.name = 'PublicStoryTextFetchError';
    this.urlAttempts =
      urlAttempts ??
      attemptedUrls.map((url, i) => ({
        url,
        detail:
          causes[i] instanceof Error
            ? causes[i].message
            : String(causes[i] ?? 'unknown'),
      }));
  }
}

/**
 * Load share story plain text, trying alternate public bucket URL shapes.
 */
export async function fetchPublicStoryTextForShare(
  env: PublicStoryBucketEnv,
  storyId: string,
  createdAtIso: string
): Promise<{ text: string; textUrl: string }> {
  const candidates = buildPublicStoryTextUrlCandidates(
    env,
    storyId,
    createdAtIso
  );
  if (candidates.length === 0) {
    throw new PublicStoryTextFetchError(
      'Invalid share link parameters',
      [],
      []
    );
  }
  const causes: unknown[] = [];
  const urlAttempts: PublicStoryUrlAttemptFailure[] = [];
  for (const textUrl of candidates) {
    try {
      const text = await fetchPublicStoryAssetText(textUrl);
      return { text, textUrl };
    } catch (e) {
      causes.push(e);
      urlAttempts.push({
        url: textUrl,
        detail:
          e instanceof Error ? e.message : String(e ?? 'unknown fetch error'),
      });
    }
  }
  throw new PublicStoryTextFetchError(
    'Could not fetch public story text',
    candidates,
    causes,
    urlAttempts
  );
}

/** Probe whether the public MP3 exists (used before share sheet). */
export async function probePublicStoryAudioReady(url: string): Promise<boolean> {
  if (audioReadyCache.has(url)) {
    return true;
  }

  const ready = await withTimeout(
    probePublicStoryAudioReadyUncached(url),
    PUBLIC_STORY_AUDIO_READY_TIMEOUT_MS,
    false
  );
  if (ready) {
    audioReadyCache.add(url);
  }
  return ready;
}

type AudioProbeOutcome = 'ready' | 'not-ready' | 'fallback';

function classifySuccessfulProbeStatus(status: number): AudioProbeOutcome {
  if (status >= 200 && status < 300) {
    return 'ready';
  }
  if (status === 403 || status === 404 || status === 410) {
    return 'not-ready';
  }
  return 'fallback';
}

function classifyFinalProbeStatus(status: number): boolean {
  return status >= 200 && status < 300;
}

async function probePublicStoryAudioReadyUncached(url: string): Promise<boolean> {
  const nativeHead = await probeAudioHeadViaCapacitor(url);
  if (nativeHead === 'ready') {
    return true;
  }
  if (nativeHead === 'not-ready') {
    return false;
  }

  const webHead = await probeAudioHeadViaFetch(url);
  if (webHead === 'ready') {
    return true;
  }
  if (webHead === 'not-ready') {
    return false;
  }

  return probeAudioRangeViaFetch(url);
}

async function probeAudioHeadViaCapacitor(
  url: string
): Promise<AudioProbeOutcome> {
  try {
    const res = await CapacitorHttp.request({
      url,
      method: 'HEAD',
      headers: PUBLIC_STORY_AUDIO_PROBE_HEADERS,
    });
    return classifySuccessfulProbeStatus(res.status);
  } catch {
    return 'fallback';
  }
}

async function probeAudioHeadViaFetch(url: string): Promise<AudioProbeOutcome> {
  try {
    const res = await fetch(url, {
      method: 'HEAD',
      headers: PUBLIC_STORY_AUDIO_PROBE_HEADERS,
    });
    return classifySuccessfulProbeStatus(res.status);
  } catch {
    return 'fallback';
  }
}

async function probeAudioRangeViaFetch(url: string): Promise<boolean> {
  try {
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        ...PUBLIC_STORY_AUDIO_PROBE_HEADERS,
        Range: 'bytes=0-0',
      },
    });
    return classifyFinalProbeStatus(res.status);
  } catch {
    return false;
  }
}

function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  fallback: T
): Promise<T> {
  return new Promise((resolve) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (!settled) {
        settled = true;
        resolve(fallback);
      }
    }, ms);

    promise.then(
      (value) => {
        if (!settled) {
          settled = true;
          clearTimeout(timer);
          resolve(value);
        }
      },
      () => {
        if (!settled) {
          settled = true;
          clearTimeout(timer);
          resolve(fallback);
        }
      }
    );
  });
}
